import { useQuery } from '@tanstack/react-query';
import api, { DocumentSignEnvelopeListResponse, DocumentSignEnvelopeStatus } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseEnvelopesParams {
  /**
   * Vault holder (workspace) to scope the list to. When `null` the hook
   * stays idle (no fetch) — the page is responsible for showing the
   * "choose a workspace" empty state in that case.
   */
  holderId: string | null;
  /**
   * Archive scope. Default `'unarchived'` (server side) matches the
   * everyday list view; `'archived'` powers the Archived filter pill.
   */
  archiveScope?: 'unarchived' | 'archived' | 'all';
  status?: DocumentSignEnvelopeStatus[];
  search?: string;
  sortBy?: 'created_at' | 'sent_at' | 'completed_at';
  limit?: number;
  offset?: number;
}

interface UseEnvelopesReturn {
  envelopes: DocumentSignEnvelopeListResponse['envelopes'];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useEnvelopes(params: UseEnvelopesParams): UseEnvelopesReturn {
  const filterKey = {
    holderId: params.holderId ?? null,
    archiveScope: params.archiveScope ?? null,
    status: params.status ?? null,
    search: params.search ?? null,
    sortBy: params.sortBy ?? null,
    limit: params.limit ?? null,
    offset: params.offset ?? null,
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.documentSign.envelopeList(filterKey),
    queryFn: () => api.listDocumentSignEnvelopes({ ...params, holderId: params.holderId! }),
    placeholderData: (prev) => prev,
    enabled: !!params.holderId,
  });

  return {
    envelopes: data?.envelopes ?? [],
    total: data?.total ?? 0,
    limit: data?.limit ?? params.limit ?? 20,
    offset: data?.offset ?? params.offset ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load envelopes' : null,
    refresh: () => {
      refetch();
    },
  };
}
